export type Inches = number & {__brand: 'inches'};
export type DisplayMatrix = RenderV2_DisplayItem[][];
export type CustomMatrix = RenderV2_AbsoluteItem[][];

/**
 * Represents all of the renderable component dimensions of a frame in inches.
 */
export interface RenderV2Object {
    moulding: RenderV2_Moulding;
    fillet?: RenderV2_Moulding;

    mat1Color: string; //HTML Hex
    mat1ImageURL?: string | null; // If no url, fall back to use color

    railLeft: Inches;
    railRight: Inches;
    railTop: Inches;
    railBottom: Inches;

    /**
     * Display item matrix, example: https://www.diplomaframe.com/corn/double-diploma-frame-76480.aspx would be:
            [
                [RenderV2_DisplayItem1],
                [RenderV2_DisplayItem2],
            ];

            2 columns and 2 rows:
            [
                [RenderV2_DisplayItem1, RenderV2_DisplayItem2],
                [RenderV2_DisplayItem3, RenderV2_DisplayItem4],
            ];

            2 columns and 3 rows:
            [
                [RenderV2_DisplayItem1, RenderV2_DisplayItem2],
                [RenderV2_DisplayItem3, RenderV2_DisplayItem4],
                [RenderV2_DisplayItem5, RenderV2_DisplayItem6],
            ];
     */
    displayMatrix: DisplayMatrix; // the display items

    customMatrix?: CustomMatrix | null; // absolute positioned items

    spiritMode?: Spirit | null;
}

interface Spirit {
    topCutWidth: number;
    bottomCutWidth: number;
    outerColor: string;
    innerColor: string;
}

interface Mat2ImageUrl extends Omit<RenderV2_Moulding, 'mouldingThickness'> {}

/**
 * Represents the documents / display items e.g. document, certificate, tassel, photo, lino, insignia
 */
export interface RenderV2_DisplayItem {
    displayType: DisplayTypeEnum;
    /**
     * Width in inches.
     * - Doc: must be finite and ≥ 0. `-1` (auto) is unsupported; the renderer
     *   resolves it to the documented fallback before frame/mat geometry
     *   (`document-dimensions/document-dimensions.ts`).
     * - Insignia / Lino / other: `-1` means CSS `auto` (intrinsic).
     */
    width: Inches;
    /**
     * Height in inches.
     * - Doc: must be finite and ≥ 0. `-1` (auto) is unsupported; same fallback
     *   policy as width.
     * - Insignia / Lino / other: `-1` means CSS `auto` (intrinsic).
     */
    height: Inches;
    url: string;

    /**
     * The gap in inches to the right of this display item, if another column follows this one.
     * Ignore the value on the last column, including when it is not -1.
     * -1 means this cell does not declare a gap. Cells on the same column boundary must declare the same gap.
     */
    gapRight: Inches;

    /**
     * The gap in inches below this display item, if another row follows this one.
     * Ignore the value on the last row, including when it is not -1.
     * -1 means this cell does not declare a gap. Cells on the same row boundary must declare the same gap.
     */
    gapBelow: Inches;

    /**
     * One-based CSS grid row line where this item starts.
     * This is a starting position, not a track span: 2 places the item on row 2 and leaves it one row tall.
     * A falsy value (0, NaN, or missing) falls back to line 1. Multi-track spans are not represented here.
     */
    rowStart: number;

    /**
     * One-based CSS grid column line where this item starts.
     * This is a starting position, not a track span: 2 places the item in column 2 and leaves it one column wide.
     * A falsy value (0, NaN, or missing) falls back to line 1. Multi-track spans are not represented here.
     */
    columnStart: number;

    mat2Color?: string; // HTML Hex
    mat2ImageURL?: Mat2ImageUrl | null;

    // todo handle wood fillet
}

export enum DisplayTypeEnum {
    Doc = 'Doc',
    Mat1 = 'Mat1',
    Insignia = 'Insignia',
    Lino = 'Lino',
    EngravedPlate = 'EngravedPlate',
    ShadowBox = 'ShadowBox',
}

export interface RenderV2_AbsoluteItem extends RenderV2_DisplayItem {
    top?: Inches; // for absolute positioned items
    bottom?: Inches; // for absolute positioned items
}

/**
 *
 */
export interface RenderV2_Moulding {
    mouldingThickness: Inches; // default 2.375

    // corners
    cornerTopLeftURL: string;
    cornerTopRightURL: string;
    cornerBottomRightURL: string;
    cornerBottomLeftURL: string;

    // sides
    sideTopURL: string;
    sideRightURL: string;
    sideBottomURL: string;
    sideLeftURL: string;
}
